import {
  Button,
  ButtonProps,
  Text,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Stack,
  Checkbox,
  HStack,
} from '@chakra-ui/react'
import { LegacyRef, PropsWithChildren, forwardRef, useCallback, useState } from 'react'
import { FaSteam } from 'react-icons/fa'
import { Trans } from '@lingui/react/macro'
import { Link } from 'react-router-dom'

import { useAccount } from '../hooks/useAccount'
import { clippath, colors } from '../theme/cssVariables'
import { BaseURLS } from '../api/axios'
import { useLanguagePath } from '../hooks/useI18nRoute'

type Props = { forceDisplayText?: boolean } & PropsWithChildren<ButtonProps>

export default forwardRef(function LoginButton(
  { children, onClick, forceDisplayText = false, ...props }: Props,
  ref: LegacyRef<HTMLButtonElement>,
) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isLogin, isLogining } = useAccount()

  const { minH, minW, ...restProps } = props
  const displayProps = isLogin ? props : restProps

  return (
    <>
      <Button
        ref={isLogin ? ref : undefined}
        variant={isLogin ? props.variant : 'solid'}
        size={['sm', 'sm', 'md', 'md']}
        {...displayProps}
        isDisabled={isLogin ? props.isDisabled : false}
        isLoading={isLogin ? props.isLoading : isLogining}
        onClick={isLogin ? onClick : onOpen}
        clipPath={clippath.polygon}
      >
        {isLogin || forceDisplayText ? (
          children
        ) : (
          <HStack spacing={2.5}>
            <Text>
              <Trans> Log in</Trans>
            </Text>
            <FaSteam color="inherit" />
          </HStack>
        )}
      </Button>
      {/* login declaration modal */}
      <LoginModal isOpen={isOpen} onClose={onClose} />
    </>
  )
})

export const LoginModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [isAgreeTCChecked, setIsAgreeTCChecked] = useState(false)
  const [isChecked, setIsChecked] = useState(false)
  const getPath = useLanguagePath()

  const handleLogin = useCallback(() => {
    const currentAppUrl = encodeURIComponent(window.location.href)
    window.location.href = `${BaseURLS.base}/auth/steam?client_redirect_to=${currentAppUrl}`
  }, [])

  return (
    <Modal isOpen={isOpen} isCentered onClose={onClose}>
      <ModalOverlay />
      <ModalContent bg={colors.colorB6} border={`1px solid ${colors.colorB5}`}>
        <ModalHeader textAlign={'center'} fontWeight={700}>
          <Trans>Login</Trans>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px={8}>
          <Stack spacing={6}>
            <Text>
              <Trans>
                Please accept the terms and click the button below to sign in via Steam.
              </Trans>
            </Text>
            <Stack spacing={3}>
              <Checkbox
                variant={'custom'}
                isChecked={isAgreeTCChecked}
                onChange={(e) => setIsAgreeTCChecked(e.target.checked)}
              >
                <Trans>
                  I agree with the{' '}
                  <Link to={getPath('terms-of-service')} onClick={onClose}>
                    <Text color={colors.colorA1} as="span">
                      Terms of Service
                    </Text>
                  </Link>{' '}
                  and{' '}
                  <Link to={getPath('/privacy-policy')} onClick={onClose}>
                    <Text color={colors.colorA1} as="span">
                      Privacy Policy
                    </Text>
                  </Link>
                  .
                </Trans>
              </Checkbox>
              <Checkbox
                variant={'custom'}
                isChecked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
              >
                <Trans>I am at least 18 years old</Trans>
              </Checkbox>
            </Stack>
          </Stack>
        </ModalBody>

        <ModalFooter justifyContent={'center'} mt={8}>
          <Button
            variant={'solid'}
            onClick={handleLogin}
            isDisabled={!isChecked || !isAgreeTCChecked}
          >
            <HStack spacing={2.5}>
              <Text>Steam</Text>
              <FaSteam color="inherit" size={18} />
            </HStack>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
